import Tesseract from "tesseract.js";
import { byId } from "./dom";

export interface ScannerController {
  start(): Promise<void>;
  stop(): void;
}

/** Camera capture UI with on-device OCR (Tesseract.js — runs entirely in
 *  the browser, no server, no API key).
 *
 *  OCR is a guess, not ground truth: meter faceplates are small, angled,
 *  and often glare-affected, so a misread digit is expected occasionally.
 *  The recognized text pre-fills the confirm field rather than being used
 *  directly — the resident always reviews/corrects it before it's passed
 *  into the meter-linking flow. That's a deliberate honesty choice, not a
 *  missing feature: this module never claims a number was read correctly,
 *  only that it made a best-effort read for the resident to check. */
export function initScanner(onUseMeterNumber: (serial: string) => void): ScannerController {
  const video = byId<HTMLVideoElement>("scanner-video");
  const fallback = byId("scanner-fallback");
  const fallbackText = byId("scanner-fallback-text");
  const viewport = byId("scanner-viewport");
  const captureBtn = byId<HTMLButtonElement>("capture-btn");
  const toggleCameraBtn = byId<HTMLButtonElement>("toggle-camera-btn");
  const retryCameraBtn = byId<HTMLButtonElement>("retry-camera-btn");
  const captureStrip = byId("capture-strip");
  const scannerCount = byId("scanner-count");
  const confirmSection = byId("scanner-confirm");
  const confirmInput = byId<HTMLInputElement>("scanner-confirm-input");
  const confirmBtn = byId<HTMLButtonElement>("scanner-confirm-btn");
  const confirmNote = byId("scanner-confirm-note");
  const ocrStatus = byId("scanner-ocr-status");

  let stream: MediaStream | null = null;
  let captureCount = 0;
  let worker: Tesseract.Worker | null = null;
  let workerReady: Promise<Tesseract.Worker> | null = null;

  function setFallback(visible: boolean, message?: string): void {
    fallback.hidden = !visible;
    video.style.visibility = visible ? "hidden" : "visible";
    if (message) fallbackText.textContent = message;
    captureBtn.disabled = visible;
  }

  async function start(): Promise<void> {
    if (stream) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setFallback(true, "This browser does not support camera capture. Try a recent version of Chrome or Safari.");
      return;
    }

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      video.srcObject = stream;
      setFallback(false);
      toggleCameraBtn.textContent = "Stop Camera";
    } catch (err) {
      stream = null;
      const name = err instanceof DOMException ? err.name : "";
      if (name === "NotAllowedError") {
        setFallback(true, "Camera access was denied. Allow camera permission in your browser settings, then try again.");
      } else if (name === "NotFoundError") {
        setFallback(true, "No camera was found on this device.");
      } else if (name === "NotReadableError") {
        setFallback(true, "The camera is already in use by another app.");
      } else {
        setFallback(true, "Camera access is off. Enable camera permission to capture meter readings.");
      }
    }
  }

  function stop(): void {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
      video.srcObject = null;
    }
    toggleCameraBtn.textContent = "Start Camera";
  }

  /** Lazily created once, on the first capture, and kept alive for the
   *  session — creating a Tesseract worker downloads the OCR engine and
   *  language data, so we don't want to pay that cost until it's actually
   *  needed, but also don't want to repeat it on every capture. */
  async function getWorker(): Promise<Tesseract.Worker> {
    if (worker) return worker;
    if (!workerReady) {
      workerReady = Tesseract.createWorker("eng").then(async (w) => {
        // Meter serials are digits (occasionally with dashes) — restricting
        // the recognizer to that alphabet meaningfully improves accuracy
        // over general text recognition.
        await w.setParameters({ tessedit_char_whitelist: "0123456789-" });
        worker = w;
        return w;
      });
    }
    return workerReady;
  }

  /** Extracts the longest run of digits/dashes from the raw OCR text —
   *  meter serials are a single contiguous number, and this discards
   *  stray noise (frame edges, reflections) that Tesseract sometimes
   *  reads as a stray character. */
  function extractSerialGuess(rawText: string): string {
    const candidates = rawText.match(/[0-9-]{6,}/g);
    if (!candidates || candidates.length === 0) return "";
    return candidates.sort((a, b) => b.length - a.length)[0].trim();
  }

  async function runOcr(canvas: HTMLCanvasElement): Promise<void> {
    ocrStatus.hidden = false;
    ocrStatus.textContent = "Reading meter number\u2026";
    confirmBtn.disabled = true;

    try {
      const w = await getWorker();
      const { data } = await w.recognize(canvas);
      const guess = extractSerialGuess(data.text);
      if (guess) {
        confirmInput.value = guess;
        confirmNote.textContent = "Best guess from the photo \u2014 check it against the meter and correct anything wrong.";
      } else {
        confirmInput.value = "";
        confirmNote.textContent = "Couldn't make out a number in that photo \u2014 type it in manually.";
      }
    } catch {
      confirmInput.value = "";
      confirmNote.textContent = "Couldn't read the photo automatically \u2014 type the number in manually.";
    } finally {
      ocrStatus.hidden = true;
      confirmBtn.disabled = false;
      confirmInput.focus();
      confirmInput.select();
    }
  }

  function capture(): void {
    if (!stream || video.videoWidth === 0) return;

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);

    const thumb = document.createElement("div");
    thumb.className = "capture-thumb";
    const img = document.createElement("img");
    img.src = dataUrl;
    img.alt = `Meter capture ${captureCount + 1}`;
    thumb.appendChild(img);
    captureStrip.prepend(thumb);

    captureCount += 1;
    scannerCount.textContent = `${captureCount} captured`;

    viewport.style.filter = "brightness(1.6)";
    setTimeout(() => {
      viewport.style.filter = "";
    }, 120);

    confirmSection.hidden = false;
    void runOcr(canvas);
  }

  toggleCameraBtn.addEventListener("click", () => {
    if (stream) {
      stop();
    } else {
      start();
    }
  });

  retryCameraBtn.addEventListener("click", () => {
    start();
  });
  captureBtn.addEventListener("click", capture);

  confirmBtn.addEventListener("click", () => {
    const serial = confirmInput.value.trim();
    if (!serial) {
      confirmInput.focus();
      return;
    }
    onUseMeterNumber(serial);
    confirmInput.value = "";
    confirmSection.hidden = true;
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
  });

  return { start, stop };
}
